"use client";

import { useId, useState } from "react";

import { ProfileDisclosureChevron } from "./ProfileDisclosureChevron";
import type { ServicesPricingOfferingDisplay } from "./profileServicesPricingDisplay";

export type ServicesPricingOfferingViewAllItemLabels = Readonly<{
  collapseLabel: (title: string) => string;
  deliverablesTitle: string;
  expandLabel: (title: string) => string;
  industriesLabel: string;
  ratingLabel: (rating: number) => string;
  reviewedByLabel: string;
  reviewsLabel: (count: number) => string;
  timesSoldLabel: (count: number) => string;
}>;

export type ServicesPricingOfferingViewAllItemProps = Readonly<{
  labels: ServicesPricingOfferingViewAllItemLabels;
  offering: ServicesPricingOfferingDisplay;
}>;

export function ServicesPricingOfferingViewAllItem({
  labels,
  offering,
}: ServicesPricingOfferingViewAllItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const detailsId = useId();
  const hasDetails = offering.deliverables.length > 0 || offering.industries.length > 0;
  const hasReputation = offering.averageRating !== undefined || offering.reviewCount !== undefined;
  const hasStats = hasReputation || offering.purchaseCount !== undefined;

  const summary = (
    <div className="profile-services-pricing-view-all-item__summary">
      <div className="profile-services-pricing-view-all-item__main">
        <div className="profile-services-pricing-view-all-item__title-block">
          <strong>{offering.title}</strong>
          {offering.tagLabel ? (
            <span className="profile-services-pricing-offering__tag">{offering.tagLabel}</span>
          ) : null}
        </div>
        {offering.description ? <p>{offering.description}</p> : null}
      </div>
      <div className="profile-services-pricing-view-all-item__commercial">
        <strong>{offering.priceLabel}</strong>
        {offering.turnaroundLabel ? <span>{offering.turnaroundLabel}</span> : null}
      </div>
      {hasStats ? (
        <div className="profile-services-pricing-view-all-item__meta">
          {hasReputation ? (
            <span className="profile-services-pricing-view-all-item__rating">
              {offering.averageRating !== undefined ? (
                <>
                  <span
                    className="profile-services-pricing-view-all-item__stars"
                    aria-label={labels.ratingLabel(offering.averageRating)}
                  >
                    {Array.from({ length: 5 }, (_, index) => (
                      <span
                        aria-hidden="true"
                        className={index < Math.floor(offering.averageRating!)
                          ? "profile-services-pricing-view-all-item__star profile-services-pricing-view-all-item__star--filled"
                          : "profile-services-pricing-view-all-item__star"}
                        key={index}
                      >★</span>
                    ))}
                  </span>
                  <span>{offering.averageRating.toFixed(1)}</span>
                </>
              ) : null}
              {offering.reviewCount !== undefined ? (
                <span>({labels.reviewsLabel(offering.reviewCount)})</span>
              ) : null}
            </span>
          ) : null}
          {hasReputation && offering.purchaseCount !== undefined ? (
            <span className="profile-services-pricing-view-all-item__separator" aria-hidden="true">•</span>
          ) : null}
          {offering.purchaseCount !== undefined ? (
            <span>{labels.timesSoldLabel(offering.purchaseCount)}</span>
          ) : null}
        </div>
      ) : null}
      {offering.featuredReviewerInitials?.length || hasDetails ? (
        <div className="profile-services-pricing-view-all-item__footer">
          {offering.featuredReviewerInitials?.length ? (
            <div className="profile-services-pricing-view-all-item__reviewers">
              <span>{labels.reviewedByLabel}</span>
              <span className="profile-services-pricing-view-all-item__reviewer-initials">
                {offering.featuredReviewerInitials.slice(0, 4).map((initials, index) => (
                  <span data-avatar-index={index} key={`${offering.id}-${initials}`}>{initials}</span>
                ))}
              </span>
            </div>
          ) : null}
          {hasDetails ? (
            <ProfileDisclosureChevron
              className="profile-services-pricing-view-all-item__chevron"
              expanded={isExpanded}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );

  return (
    <article
      className="profile-services-pricing-view-all-item"
      data-expanded={isExpanded ? "true" : "false"}
    >
      {hasDetails ? (
        <button
          type="button"
          className="profile-services-pricing-view-all-item__toggle"
          aria-controls={detailsId}
          aria-expanded={isExpanded}
          aria-label={isExpanded
            ? labels.collapseLabel(offering.title)
            : labels.expandLabel(offering.title)}
          onClick={() => setIsExpanded((current) => !current)}
        >
          {summary}
        </button>
      ) : summary}
      {hasDetails && isExpanded ? (
        <div className="profile-services-pricing-view-all-item__details" id={detailsId}>
          {offering.deliverables.length > 0 ? (
            <div>
              <strong>{labels.deliverablesTitle}</strong>
              <ul>
                {offering.deliverables.map((deliverable) => (
                  <li key={deliverable}><span aria-hidden="true">✓</span>{deliverable}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {offering.industries.length > 0 ? (
            <div className="profile-services-pricing-view-all-item__industries">
              <strong>{labels.industriesLabel}</strong>
              <span>
                {offering.industries.map((industry) => <span key={industry}>{industry}</span>)}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
